import React, { useState } from "react";
import { Mail, Send, PartyPopper } from "lucide-react";
import Reveal from "../components/Reveal";
import { Overline } from "../components/primitives";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const CONTACT_OPTS = [
  { value: "text", label: "Text message" },
  { value: "phone", label: "Phone call" },
  { value: "email", label: "Email" },
];

const emptyForm = { full_name: "", email: "", phone: "", city_state: "", preferred_contact: "", message: "", consent: false };

export default function Contact() {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.phone.trim() || !form.city_state.trim()) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!form.preferred_contact) {
      toast.error("Please choose how you'd prefer to be contacted.");
      return;
    }
    if (!form.consent) {
      toast.error("Please agree to be contacted to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      window.scrollTo({ top: document.getElementById("contact-form")?.offsetTop - 80 || 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full h-12 rounded-xl border border-black/10 px-4 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary transition-shadow";

  return (
    <div className="pt-16 md:pt-20">
      <section id="contact-form" className="py-20 md:py-28 scroll-mt-24">
        <div className="max-w-2xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="text-center">
              <Overline>Contact</Overline>
              <h1 className="mt-4 font-display font-extrabold tracking-tighter text-4xl md:text-6xl leading-[0.98] uppercase">
                Ready For Your Next Adventure?
              </h1>
              <p className="mt-6 text-base md:text-lg text-foreground/70 leading-relaxed">
                Have questions about the El Salvador Fitness &amp; Adventure Retreat? Complete the form below, and our team will contact you with everything you need to know.
              </p>
            </div>
          </Reveal>

          {done ? (
            <Reveal>
              <div data-testid="contact-success" className="mt-12 rounded-3xl bg-white border border-black/10 p-8 md:p-10 text-center shadow-sm">
                <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 grid place-items-center text-primary">
                  <PartyPopper className="w-7 h-7" />
                </div>
                <p className="mt-6 text-lg text-foreground/80 leading-relaxed">
                  Thank you! 🌴 We received your information. A member of the La Vida Is Travel Fit team will contact you soon. Get ready for your next adventure! 🇸🇻
                </p>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={submit} className="mt-12 rounded-3xl bg-white border border-black/10 p-6 md:p-8 space-y-5 shadow-sm" data-testid="contact-lead-form">
                <div>
                  <label className="text-sm font-semibold">Full Name<span className="text-primary">*</span></label>
                  <input className={`${inputCls} mt-1.5`} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Enter your full name" data-testid="lead-full-name" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-semibold">Email Address<span className="text-primary">*</span></label>
                    <input type="email" className={`${inputCls} mt-1.5`} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Enter your email" data-testid="lead-email" />
                  </div>
                  <div>
                    <label className="text-sm font-semibold">Phone Number<span className="text-primary">*</span></label>
                    <input className={`${inputCls} mt-1.5`} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Enter your phone number" data-testid="lead-phone" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold">Where are you traveling from?<span className="text-primary">*</span></label>
                  <input className={`${inputCls} mt-1.5`} value={form.city_state} onChange={(e) => set("city_state", e.target.value)} placeholder="City and state" data-testid="lead-city-state" />
                </div>
                <div>
                  <label className="text-sm font-semibold">How would you prefer to be contacted?<span className="text-primary">*</span></label>
                  <div className="mt-2.5 flex flex-col sm:flex-row gap-3" data-testid="lead-preferred-contact">
                    {CONTACT_OPTS.map((o) => (
                      <label key={o.value} className={`flex items-center gap-2.5 rounded-xl border px-4 h-12 flex-1 cursor-pointer transition-colors ${form.preferred_contact === o.value ? "border-primary bg-primary/5" : "border-black/10 hover:bg-black/[0.02]"}`}>
                        <input type="radio" name="preferred_contact" value={o.value} checked={form.preferred_contact === o.value} onChange={(e) => set("preferred_contact", e.target.value)} className="accent-primary w-4 h-4" data-testid={`lead-contact-${o.value}`} />
                        <span className="text-sm font-medium">{o.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold">Questions or comments</label>
                  <textarea rows={5} className="mt-1.5 w-full rounded-xl border border-black/10 p-4 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary transition-shadow" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us how we can help" data-testid="lead-message" />
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="accent-primary w-5 h-5 mt-0.5 shrink-0" data-testid="lead-consent" />
                  <span className="text-xs text-foreground/60 leading-relaxed">
                    I agree to receive information about this retreat by email, phone, or text. Message and data rates may apply. Consent is not required to make a purchase.
                  </span>
                </label>
                <button type="submit" disabled={submitting} data-testid="lead-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-white h-14 text-base font-semibold tracking-wide uppercase transition-[transform,background-color,box-shadow] duration-200 hover:bg-[#B84726] hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/20 disabled:opacity-50 disabled:pointer-events-none">
                  <Send className="w-5 h-5" /> {submitting ? "Sending…" : "Send My Information"}
                </button>
                <p className="text-center text-sm text-foreground/50">We typically respond within one business day.</p>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
